/**
 * =============================================================================
 * QUESTION QUEUE COMPONENT
 * =============================================================================
 *
 * Main orchestrator component for displaying and managing the question queue.
 * Fetches questions on mount, supports filtering and pagination.
 *
 * ARCHITECTURE DECISION: This component uses BigDesign's Table component with
 * column render functions rather than a separate QuestionRow component. This
 * approach was chosen because:
 * 1. BigDesign's Table is the vendor-recommended pattern for data grids
 * 2. It provides built-in accessibility and responsive behavior
 * 3. Reduces code duplication by leveraging the framework
 * 4. The extracted QuestionActions component handles row-level action state
 *
 * See Task 2.4.3 peer review analysis for full rationale.
 *
 * P1-17: Extracted sub-components for maintainability:
 * - QuestionActions: Row action buttons
 * - QuestionDetailModal: Question detail view with image gallery
 * - QuestionFilters: Status filter and sort dropdowns
 * - QualityBadge: Quality score display
 * - TableSkeleton: Loading skeleton
 *
 * ARCHITECTURE REFERENCE: Section 10 - Admin Dashboard
 * PHASE_0B_SPEC.md: Task 2.4.3
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { Box, Button, Flex, FlexItem, H2, Panel, Table, Text } from '@bigcommerce/big-design';
import { ArrowBackIcon, ArrowForwardIcon, RefreshIcon } from '@bigcommerce/big-design-icons';
import { StatusBadge } from './StatusBadge';
import { QualityBadge } from './QualityBadge';
import { QuestionActions } from './QuestionActions';
import { QuestionDetailModal } from './QuestionDetailModal';
import { QuestionRejectModal } from './QuestionRejectModal';
import {
  canReapplyQuestionStatusIssue,
  hasCommittedQuestionStatusPrefix,
  isAmbiguousQuestionStatusFailure,
  isConvergedQuestionStatusIssue,
  isQuestionExpectedStateStaleTabError,
  QuestionStatusWriteNotice,
  readQuestionPatchSubOps,
  readQuestionStatusConflict,
  type QuestionStatusWriteIssue,
  type QuestionStatusWriteOrigin,
} from './QuestionStatusWriteNotice';
import { QuestionFilters } from './QuestionFilters';
import { TableSkeleton, type TableSkeletonColumn } from './TableSkeleton';
import { QuestionWorkspace, type QuestionWorkspaceHandle } from './QuestionWorkspace';
import { useSession } from '../../hooks/useSession';
import { useToast } from '../../hooks/useToast';
import { adminApi } from '../../api/client';
import { formatDate } from '../../utils/formatters';
import { logger } from '../../lib/logger';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BREAKPOINTS } from '../../constants/breakpoints';
import type {
  AdminQuestionListItem,
  AdminQuestionFilters,
  QuestionStats,
  PaginationMeta,
  QuestionRejectionReason,
  QuestionStatus,
} from '@find-my-part/types';
import { QUESTION_STATUSES } from '@find-my-part/types';
import type { QueueView } from '../Shell/location';
import { tokens } from '../../tokens';
import { EmptyState } from '../common/EmptyState';
import { PageHeader } from '../common/PageHeader';
import { Toolbar } from '../common/Toolbar';

/**
 * Items per page.
 */
const ITEMS_PER_PAGE = 20;

/**
 * Maximum description length before truncation.
 */
const MAX_DESCRIPTION_LENGTH = 80;

/** Union of queue column hashes — keeps the in-component renderer record exhaustive. */
export type QueueColumnHash =
  | 'title'
  | 'poster'
  | 'date'
  | 'replies'
  | 'images'
  | 'quality'
  | 'status'
  | 'actions';

/**
 * B-6 WP-7a (design-v3 §H): the queue table's column identity + shape,
 * single-sourced at module level so the Table columns builder and the
 * loading skeleton can never drift apart. `shape` feeds BOTH: a fixed
 * `{ width }` would flow to the Table column AND its skeleton cell; `'flex'`
 * (every column today — none declares a width) leaves the Table column
 * unsized and renders a growing skeleton cell. Adding a column is one entry
 * here plus one renderer in the component's `columnRenderers` record (the
 * `QueueColumnHash` union makes TypeScript demand it); the parity test in
 * QuestionQueue.test.tsx pins rendered column count == spec length.
 */
export const QUEUE_COLUMN_SPEC: ReadonlyArray<{
  hash: QueueColumnHash;
  header: string;
  shape: TableSkeletonColumn;
}> = [
  { hash: 'title', header: 'Question', shape: 'flex' },
  { hash: 'poster', header: 'Poster', shape: 'flex' },
  { hash: 'date', header: 'Date', shape: 'flex' },
  { hash: 'replies', header: 'Replies', shape: 'flex' },
  { hash: 'images', header: 'Images', shape: 'flex' },
  { hash: 'quality', header: 'Quality', shape: 'flex' },
  { hash: 'status', header: 'Status', shape: 'flex' },
  { hash: 'actions', header: 'Actions', shape: 'flex' },
];

/** Skeleton cell shapes derived from the spec (design-v3 §H one-source rule). */
const QUEUE_SKELETON_COLUMNS: ReadonlyArray<TableSkeletonColumn> = QUEUE_COLUMN_SPEC.map(
  (column) => column.shape
);

/**
 * ADR-025 B-2 (§3.1.6 / M8): an open-question intent dispatched by the shell
 * when a `questions` location carrying a `questionId` is navigated to (e.g.
 * the media-row → parent-question link). The `nonce` increments per dispatch
 * so a REPEAT dispatch to the same question re-fires while this section is
 * keep-alive-mounted — a bare `questionId` prop that never changes could not.
 */
export interface OpenQuestionIntent {
  questionId: string;
  /** Monotonic per-dispatch counter (starts at 1); equal nonce = already handled. */
  nonce: number;
}

/**
 * Props for QuestionQueue component.
 */
export interface QuestionQueueProps {
  /** Optional test ID for testing */
  'data-testid'?: string;
  /**
   * ADR-025 B-2: pending open-question intent from the shell (absent/null
   * outside the shell — the legacy layout passes nothing and behaviour is
   * unchanged). Resolved against the CURRENTLY LOADED rows only (M8): found →
   * the existing detail modal opens via the existing object-keyed selection;
   * not found (other page/filter) → the queue renders normally — no fetch, no
   * error, no crash (fail-safe; M14: B-2 adds zero fetches).
   */
  openQuestionIntent?: OpenQuestionIntent | null;
  /** B-3 semantic filtered queue destination. */
  view?: QueueView;
  /** Repeatable explicit destination intent; unchanged nonce is already consumed. */
  viewIntentNonce?: number;
  /** B-4 flag-ON surface selector; absent/false preserves the legacy modal. */
  workspaceEnabled?: boolean;
  /** ID-keyed selection from the shell's semantic question location. */
  selectedQuestionId?: string;
  /** Writes ID-keyed selection back to the semantic location. */
  onQuestionSelectionChange?: (questionId?: string) => void;
  /** Whether Questions is the visible keep-alive section (focus ownership). */
  isActive?: boolean;
  /** Register the workspace reply-draft boundary with AdminShell. */
  registerNavigationGuard?: (guard: (proceed: () => void) => void) => () => void;
}

/**
 * Truncate text to specified length with ellipsis.
 */
function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + '...';
}

function isKnownQuestionStatus(value: unknown): value is QuestionStatus {
  return typeof value === 'string' && (QUESTION_STATUSES as readonly string[]).includes(value);
}

function hasCommittedStatusSubOperation(
  subOps: ReturnType<typeof readQuestionPatchSubOps>
): boolean {
  return subOps.some((subOp) => subOp.op === 'status' && subOp.outcome === 'committed');
}

/** Reconcile the three exact status buckets exposed by the queue summary. */
function transitionQuestionStats(
  stats: QuestionStats,
  fromStatus: QuestionStatus,
  toStatus: QuestionStatus
): QuestionStats {
  if (fromStatus === toStatus) return stats;
  return {
    ...stats,
    pendingCount: Math.max(
      0,
      stats.pendingCount +
        (toStatus === 'PENDING_REVIEW' ? 1 : 0) -
        (fromStatus === 'PENDING_REVIEW' ? 1 : 0)
    ),
    openCount: Math.max(
      0,
      stats.openCount + (toStatus === 'OPEN' ? 1 : 0) - (fromStatus === 'OPEN' ? 1 : 0)
    ),
    resolvedCount: Math.max(
      0,
      stats.resolvedCount + (toStatus === 'RESOLVED' ? 1 : 0) - (fromStatus === 'RESOLVED' ? 1 : 0)
    ),
  };
}

/**
 * QuestionQueue component.
 *
 * Provides a complete question management interface with:
 * - Question table with all relevant columns including description
 * - Status filter dropdown
 * - Stats display (pending, open, resolved counts)
 * - Pagination controls (prev/next)
 * - Approve/reject actions
 * - Refresh button
 * - Loading skeletons and error states
 * - Question detail modal with image gallery
 *
 * @example
 * ```tsx
 * <SessionProvider>
 *   <QuestionQueue />
 * </SessionProvider>
 * ```
 */
export function QuestionQueue({
  'data-testid': testId,
  openQuestionIntent,
  view,
  viewIntentNonce,
  workspaceEnabled = false,
  selectedQuestionId,
  onQuestionSelectionChange,
  isActive = true,
  registerNavigationGuard,
}: QuestionQueueProps): React.ReactElement {
  // Session context
  const { token, isAuthenticated } = useSession();

  // Toast notifications (P1-7)
  const { showSuccess, showError } = useToast();

  // State
  const [questions, setQuestions] = useState<AdminQuestionListItem[]>([]);
  const [stats, setStats] = useState<QuestionStats | null>(null);
  const [pagination, setPagination] = useState<PaginationMeta | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [unansweredOnly, setUnansweredOnly] = useState<boolean>(false);
  const [hasImagesOnly, setHasImagesOnly] = useState<boolean>(false);
  const [needsReviewOnly, setNeedsReviewOnly] = useState<boolean>(view === 'needsReview');
  const [pendingImagesOnly, setPendingImagesOnly] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>(
    view === 'pending' ? 'PENDING_REVIEW' : ''
  );
  const [sortValue, setSortValue] = useState<string>('createdAt:desc'); // P1-10: Default sort
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedQuestion, setSelectedQuestion] = useState<AdminQuestionListItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // P1-8: Track pending actions to prevent race conditions with concurrent clicks
  const [pendingActions, setPendingActions] = useState<Set<string>>(new Set());
  const [statusWriteIssues, setStatusWriteIssues] = useState<
    Record<string, QuestionStatusWriteIssue>
  >({});
  const statusWriteEventSequenceRef = useRef(0);
  const queueInteractionLocked =
    pendingActions.size > 0 ||
    Object.values(statusWriteIssues).some((issue) => issue.origin === 'queue');
  // ADR-025 A-Q1: the question awaiting a rejection reason (dialog open when set)
  const [rejectTarget, setRejectTarget] = useState<AdminQuestionListItem | null>(null);
  const [rejectOrigin, setRejectOrigin] = useState<QuestionStatusWriteOrigin>('queue');
  const [isRejectSubmitting, setIsRejectSubmitting] = useState(false);
  // A-Q1 / peer-review F2: stable focus fallback for the reject dialog — a
  // confirmed rejection unmounts its trigger (row transforms / detail modal
  // closes), so A-0 falls back here instead of dropping focus to <body>.
  const refreshBtnRef = useRef<HTMLButtonElement>(null);
  // Successful queue-origin status writes can replace or remove their row
  // during the awaited refetch. Restore only after pending/loading settle so
  // the surviving row control (or stable Refresh fallback) is focusable.
  const pendingQueueStatusFocusRef = useRef<string | null>(null);
  const workspaceRef = useRef<QuestionWorkspaceHandle>(null);
  const previousWorkspaceQuestionId = useRef<string | undefined>(selectedQuestionId);
  const previousIsActive = useRef(isActive);
  const narrowQueueViewport = useRef<{ left: number; top: number } | null>(null);
  const [lastResolvedWorkspaceQuestion, setLastResolvedWorkspaceQuestion] =
    useState<AdminQuestionListItem | null>(null);
  const isNarrowWorkspace = useIsMobile(BREAKPOINTS.DESKTOP);
  const isNarrowWorkspaceRef = useRef(isNarrowWorkspace);

  /**
   * Fetch questions from the API.
   */
  const fetchQuestions = useCallback(async (): Promise<void> => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

    try {
      const filters: AdminQuestionFilters = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
      };

      if (debouncedSearch) {
        filters.search = debouncedSearch;
      }

      if (unansweredOnly) {
        filters.unanswered = true;
      }

      if (hasImagesOnly) {
        filters.hasImages = true;
      }

      if (needsReviewOnly) {
        filters.needsReview = true;
      }

      if (pendingImagesOnly) {
        filters.pendingImages = true;
      }

      if (statusFilter) {
        filters.status = statusFilter as AdminQuestionFilters['status'];
      }

      // P1-10: Parse sort value and add to filters
      if (sortValue) {
        const [sortBy, sortOrder] = sortValue.split(':') as [
          AdminQuestionFilters['sortBy'],
          AdminQuestionFilters['sortOrder'],
        ];
        filters.sortBy = sortBy;
        filters.sortOrder = sortOrder;
      }

      const response = await adminApi.questions.list(token, filters);

      if (response.success) {
        setQuestions(response.data.questions);
        setStats(response.data.stats);
        setPagination(response.data.pagination);
      } else {
        setError(response.error.message);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch questions');
    } finally {
      setIsLoading(false);
    }
  }, [
    token,
    currentPage,
    debouncedSearch,
    unansweredOnly,
    hasImagesOnly,
    needsReviewOnly,
    pendingImagesOnly,
    statusFilter,
    sortValue,
  ]);

  // Fetch questions on mount and when filters change
  useEffect(() => {
    if (isAuthenticated && token) {
      fetchQuestions();
    }
  }, [isAuthenticated, token, fetchQuestions]);

  // Debounce search query (300ms). Pagination is reset ONLY when the
  // debounced search actually changes — never on the initial-mount fire,
  // and never if the user types and reverts back to the same value before
  // the timer settles. Without this guard, the mount timer would call
  // `setCurrentPage(1)` 300ms after mount and clobber any pagination the
  // user navigated to within that window (e.g. clicking Next within 300ms
  // of landing on the queue).
  //
  // Compared to a "skip first fire" ref, this guard is robust against the
  // case where the user changes the input BEFORE the initial mount timer
  // fires — the effect cleanup clears the mount timer, so the next timer
  // is technically the "first" one to fire even though it represents a
  // real user change.
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const lastCommittedSearch = useRef<string>('');
  useEffect(() => {
    if (queueInteractionLocked) return undefined;
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      if (lastCommittedSearch.current !== searchQuery) {
        lastCommittedSearch.current = searchQuery;
        setCurrentPage(1);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [queueInteractionLocked, searchQuery]);

  // B-3 filtered semantic navigation: every explicit destination carries a
  // fresh nonce so an identical Overview-tile click re-applies after local
  // filter drift. Bare rail re-entry keeps the nonce unchanged and preserves
  // the retained queue state.
  useEffect(() => {
    if (!view || queueInteractionLocked) return;
    setSearchQuery('');
    setDebouncedSearch('');
    lastCommittedSearch.current = '';
    setUnansweredOnly(false);
    setHasImagesOnly(false);
    setPendingImagesOnly(false);
    setNeedsReviewOnly(view === 'needsReview');
    setStatusFilter(view === 'pending' ? 'PENDING_REVIEW' : '');
    setCurrentPage(1);
  }, [queueInteractionLocked, view, viewIntentNonce]);
  // ADR-025 B-2 (§3.1.6 / M8): resolve a shell-dispatched open-question intent
  // against the CURRENTLY LOADED rows. Each dispatch carries a fresh nonce, so
  // a repeat jump to the same question re-fires under keep-alive; a nonce is
  // consumed exactly once. While a load is in flight the intent stays PENDING
  // (the effect re-runs when `isLoading` settles), so a jump that first
  // activates this section resolves against the rows that load lands — never
  // against the transient empty state. Not found (other page/filter) →
  // fail-safe no-op: the operator lands on the queue normally, no fetch, no
  // error (M14: B-2 adds zero fetches — deliberately NO by-id lookup here).
  const processedIntentNonce = useRef(0);
  useEffect(() => {
    if (!openQuestionIntent) return;
    if (openQuestionIntent.nonce === processedIntentNonce.current) return;
    if (isLoading) return; // pending until the in-flight load settles
    processedIntentNonce.current = openQuestionIntent.nonce;
    const match = questions.find((q) => q.questionId === openQuestionIntent.questionId);
    if (match && !workspaceEnabled) {
      setSelectedQuestion(match);
      setIsModalOpen(true);
    }
  }, [openQuestionIntent, isLoading, questions, workspaceEnabled]);

  // B-4 rule .2: an initially unknown semantic ID remains fail-safe (queue
  // only, no fetch). Once an ID has resolved, pin that object to the semantic
  // selection so pagination/filter/refetch churn cannot silently unmount a
  // dirty workspace merely because its row left the current result set.
  const currentWorkspaceQuestion =
    workspaceEnabled && selectedQuestionId
      ? questions.find((question) => question.questionId === selectedQuestionId)
      : undefined;

  useEffect(() => {
    setLastResolvedWorkspaceQuestion((previous) => {
      if (!workspaceEnabled || !selectedQuestionId) return null;
      if (currentWorkspaceQuestion) return currentWorkspaceQuestion;
      return previous?.questionId === selectedQuestionId ? previous : null;
    });
  }, [currentWorkspaceQuestion, selectedQuestionId, workspaceEnabled]);

  const workspaceQuestion =
    currentWorkspaceQuestion ??
    (lastResolvedWorkspaceQuestion?.questionId === selectedQuestionId
      ? lastResolvedWorkspaceQuestion
      : null);
  const workspaceQuestionId = workspaceQuestion?.questionId;

  useLayoutEffect(() => {
    isNarrowWorkspaceRef.current = isNarrowWorkspace;
    if (!isNarrowWorkspace) {
      narrowQueueViewport.current = null;
    }
  }, [isNarrowWorkspace]);

  // A narrow workspace replaces the queue, so it must establish a fresh page
  // origin before paint instead of inheriting the queue's vertical offset.
  // Wide split-pane selection deliberately preserves the shared page position.
  useLayoutEffect(() => {
    if (!isActive || !workspaceEnabled || !isNarrowWorkspace || !workspaceQuestionId) return;
    window.scrollTo({ left: 0, top: 0, behavior: 'auto' });
  }, [isActive, isNarrowWorkspace, workspaceEnabled, workspaceQuestionId]);

  useEffect(() => {
    if (!isActive || !registerNavigationGuard) return undefined;
    const guard = (proceed: () => void): void => {
      // A queue-origin mutation owns this view until its outcome is visible and
      // resolved. Hiding the keep-alive section would suppress the initially
      // empty alert's only announcement and strand its recovery controls.
      if (queueInteractionLocked) return;
      const workspace = workspaceRef.current;
      if (workspace) workspace.requestTransition(proceed);
      else proceed();
    };
    return registerNavigationGuard(guard);
  }, [isActive, queueInteractionLocked, registerNavigationGuard, workspaceQuestion]);

  // A page workspace is not a dialog, so focus restoration is explicit. Only
  // a close that leaves Questions visible owns this return; section navigation
  // moves focus to the destination heading instead.
  useEffect(() => {
    const previousId = previousWorkspaceQuestionId.current;
    const wasActive = previousIsActive.current;
    previousWorkspaceQuestionId.current = selectedQuestionId;
    previousIsActive.current = isActive;
    if (!isActive) {
      narrowQueueViewport.current = null;
      return undefined;
    }
    if (!workspaceEnabled || !previousId || selectedQuestionId || !wasActive) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      const trigger = document.querySelector<HTMLElement>(
        `[data-question-workspace-trigger="${previousId}"]`
      );
      (trigger ?? refreshBtnRef.current)?.focus();
      const viewport = narrowQueueViewport.current;
      narrowQueueViewport.current = null;
      if (viewport && isNarrowWorkspaceRef.current) {
        window.scrollTo({ ...viewport, behavior: 'auto' });
      }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isActive, selectedQuestionId, workspaceEnabled]);

  /**
   * Handle search input change.
   */
  const handleSearchChange = useCallback(
    (value: string): void => {
      if (!queueInteractionLocked) setSearchQuery(value);
    },
    [queueInteractionLocked]
  );

  /**
   * Handle unanswered filter toggle.
   */
  const handleUnansweredChange = useCallback(
    (checked: boolean): void => {
      if (queueInteractionLocked) return;
      setUnansweredOnly(checked);
      setCurrentPage(1);
    },
    [queueInteractionLocked]
  );

  /**
   * Handle has-images filter toggle.
   */
  const handleHasImagesChange = useCallback(
    (checked: boolean): void => {
      if (queueInteractionLocked) return;
      setHasImagesOnly(checked);
      setCurrentPage(1);
    },
    [queueInteractionLocked]
  );

  /**
   * Handle needs-review filter toggle (questions with >= 1 pending reply).
   */
  const handleNeedsReviewChange = useCallback(
    (checked: boolean): void => {
      if (queueInteractionLocked) return;
      setNeedsReviewOnly(checked);
      setCurrentPage(1);
    },
    [queueInteractionLocked]
  );

  /**
   * Handle pending-images filter change (ADR-022 §10(C) "Pending images").
   */
  const handlePendingImagesChange = useCallback(
    (checked: boolean): void => {
      if (queueInteractionLocked) return;
      setPendingImagesOnly(checked);
      setCurrentPage(1);
    },
    [queueInteractionLocked]
  );

  /**
   * Handle status filter change.
   */
  const handleStatusChange = useCallback(
    (value: string): void => {
      if (queueInteractionLocked) return;
      setStatusFilter(value);
      setCurrentPage(1); // Reset to first page when filter changes
    },
    [queueInteractionLocked]
  );

  /**
   * P1-10: Handle sort change.
   */
  const handleSortChange = useCallback(
    (value: string): void => {
      if (queueInteractionLocked) return;
      setSortValue(value);
      setCurrentPage(1); // Reset to first page when sort changes
    },
    [queueInteractionLocked]
  );

  /**
   * Handle page navigation.
   */
  const handlePrevPage = useCallback((): void => {
    if (!queueInteractionLocked && currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage, queueInteractionLocked]);

  const handleNextPage = useCallback((): void => {
    if (!queueInteractionLocked && pagination && currentPage < pagination.totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, pagination, queueInteractionLocked]);

  // A resolved workspace may intentionally outlive its row's presence on the
  // current queue page. Moderation still needs the retained object for status
  // and optimistic-stat semantics; initial unknown IDs remain unresolved.
  const resolveQuestionForAction = useCallback(
    (questionId: string): AdminQuestionListItem | undefined =>
      questions.find((question) => question.questionId === questionId) ??
      (lastResolvedWorkspaceQuestion?.questionId === questionId
        ? lastResolvedWorkspaceQuestion
        : undefined),
    [lastResolvedWorkspaceQuestion, questions]
  );

  const actionOriginFor = useCallback(
    (questionId: string): QuestionStatusWriteOrigin => {
      if (workspaceEnabled && selectedQuestionId === questionId) return 'workspace';
      if (!workspaceEnabled && isModalOpen && selectedQuestion?.questionId === questionId) {
        return 'detail';
      }
      return 'queue';
    },
    [isModalOpen, selectedQuestion, selectedQuestionId, workspaceEnabled]
  );

  const transitionLocalQuestionStatus = useCallback(
    (questionId: string, fromStatus: QuestionStatus, toStatus: QuestionStatus): void => {
      setQuestions((previous) =>
        previous.map((question) =>
          question.questionId === questionId ? { ...question, status: toStatus } : question
        )
      );
      setSelectedQuestion((previous) =>
        previous?.questionId === questionId ? { ...previous, status: toStatus } : previous
      );
      setLastResolvedWorkspaceQuestion((previous) =>
        previous?.questionId === questionId ? { ...previous, status: toStatus } : previous
      );
      setStats((previous) =>
        previous ? transitionQuestionStats(previous, fromStatus, toStatus) : previous
      );
    },
    []
  );

  const publishStatusWriteIssue = useCallback((issue: QuestionStatusWriteIssue): void => {
    const eventId = issue.eventId ?? ++statusWriteEventSequenceRef.current;
    const announcementKind = issue.announcementKind ?? issue.kind;
    setStatusWriteIssues((previous) => ({
      ...previous,
      [issue.questionId]: { ...issue, eventId, announcementKind },
    }));
  }, []);

  const clearStatusWriteIssue = useCallback((questionId: string): void => {
    setStatusWriteIssues((previous) => {
      if (!previous[questionId]) return previous;
      const next = { ...previous };
      delete next[questionId];
      return next;
    });
  }, []);

  const restoreQueueStatusFocus = useCallback((questionId: string): HTMLElement | null => {
    const approve = document.querySelector<HTMLButtonElement>(
      `[data-testid$="row-${questionId}-approve-btn"]`
    );
    if (approve && !approve.disabled) return approve;
    return (
      document.querySelector<HTMLElement>(`[data-testid$="row-${questionId}-view-btn"]`) ??
      refreshBtnRef.current
    );
  }, []);

  useEffect(() => {
    const questionId = pendingQueueStatusFocusRef.current;
    if (!questionId || pendingActions.has(questionId) || isLoading) return undefined;

    const timer = window.setTimeout(() => {
      const target = restoreQueueStatusFocus(questionId);
      if (target && document.contains(target) && !target.matches(':disabled')) {
        target.focus();
      }
      pendingQueueStatusFocusRef.current = null;
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isLoading, pendingActions, questions, restoreQueueStatusFocus]);

  /**
   * Handle question approval.
   * P1-7: Shows toast notification on success/error.
   * P1-8: Implements optimistic update - UI updates immediately, reverts on failure.
   * P1-8 Review: Added race condition prevention with pendingActions guard and per-question rollback.
   */
  const handleApprove = useCallback(
    async (
      questionId: string,
      expectedStatusOverride?: QuestionStatus,
      originOverride?: QuestionStatusWriteOrigin
    ): Promise<boolean> => {
      if (!token) return false;

      const question = resolveQuestionForAction(questionId);
      if (!question) return false;
      const baselineStatus = expectedStatusOverride ?? question.status;
      if (!isKnownQuestionStatus(baselineStatus)) return false;
      const originalStatus = baselineStatus;
      const targetStatus: QuestionStatus = 'OPEN';
      const origin = originOverride ?? actionOriginFor(questionId);

      // P1-8: Prevent concurrent actions on the same question (race condition fix)
      if (pendingActions.has(questionId)) return false;
      setPendingActions((prev) => new Set(prev).add(questionId));
      setError(null);
      transitionLocalQuestionStatus(questionId, originalStatus, targetStatus);

      try {
        const response = await adminApi.questions.updateStatus(token, questionId, targetStatus, {
          expectedStatus: originalStatus,
        });

        if (response.success) {
          clearStatusWriteIssue(questionId);
          showSuccess('Question approved successfully');
          if (origin === 'queue') pendingQueueStatusFocusRef.current = questionId;
          // P1-8: Refresh to sync with server state
          await fetchQuestions();
          return true;
        }

        const subOps = readQuestionPatchSubOps(response.error.details?.subOps);
        const statusPrefixCommitted = hasCommittedStatusSubOperation(subOps);
        const conflict = readQuestionStatusConflict(response.error);
        if (statusPrefixCommitted) {
          // The optimistic target is now known durable. Never roll it back or
          // offer a status replay; later suffix editors may supply their own
          // retained drafts through the notice's typed recovery seam.
          publishStatusWriteIssue({
            kind: 'partial',
            questionId,
            action: 'approve',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
            subOps,
          });
        } else if (conflict) {
          transitionLocalQuestionStatus(questionId, targetStatus, conflict.observed.status);
          publishStatusWriteIssue({
            kind: 'conflict',
            questionId,
            action: 'approve',
            origin,
            expectedStatus: conflict.expected.status,
            observedStatus: conflict.observed.status,
            targetStatus,
            subOps: conflict.subOps,
          });
        } else if (response.error.code === 'STALE_WRITE') {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          publishStatusWriteIssue({
            kind: 'refresh_required',
            questionId,
            action: 'approve',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
            subOps,
          });
        } else if (isQuestionExpectedStateStaleTabError(response.error)) {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          publishStatusWriteIssue({
            kind: 'stale_tab',
            questionId,
            action: 'approve',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
          });
        } else if (isAmbiguousQuestionStatusFailure(response.error)) {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          publishStatusWriteIssue({
            kind: 'ambiguous',
            questionId,
            action: 'approve',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
            subOps,
          });
        } else {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          clearStatusWriteIssue(questionId);
          showError(response.error.message);
          setError(response.error.message);
        }
        return false;
      } catch (err) {
        transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
        logger.error('APPROVE_FAILURE', err instanceof Error ? err : null, { questionId });
        publishStatusWriteIssue({
          kind: 'ambiguous',
          questionId,
          action: 'approve',
          origin,
          expectedStatus: originalStatus,
          targetStatus,
          subOps: [],
        });
        return false;
      } finally {
        // P1-8: Clear pending state to re-enable actions
        setPendingActions((prev) => {
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
      }
    },
    [
      token,
      pendingActions,
      fetchQuestions,
      showSuccess,
      showError,
      resolveQuestionForAction,
      actionOriginFor,
      clearStatusWriteIssue,
      publishStatusWriteIssue,
      transitionLocalQuestionStatus,
    ]
  );

  /**
   * Handle question rejection.
   * P1-7: Shows toast notification on success/error.
   * P1-8: Implements optimistic update - UI updates immediately, reverts on failure.
   * P1-8 Review: Added race condition prevention with pendingActions guard and per-question rollback.
   */
  const performReject = useCallback(
    // ADR-025 A-Q1 (OD-19): rejection now carries a REQUIRED controlled reason
    // (+ optional note) collected by QuestionRejectModal — the optimistic
    // update / per-question rollback / pendingActions guard below are the
    // pre-existing P1-8 machinery, unchanged.
    async (
      questionId: string,
      reason: QuestionRejectionReason,
      note?: string,
      expectedStatusOverride?: QuestionStatus,
      originOverride?: QuestionStatusWriteOrigin
    ): Promise<boolean> => {
      if (!token) return false;

      const question = resolveQuestionForAction(questionId);
      if (!question) return false;
      const baselineStatus = expectedStatusOverride ?? question.status;
      if (!isKnownQuestionStatus(baselineStatus)) return false;
      const originalStatus = baselineStatus;
      const targetStatus: QuestionStatus = 'CLOSED_REJECTED';
      const origin = originOverride ?? actionOriginFor(questionId);

      // P1-8: Prevent concurrent actions on the same question (race condition fix)
      if (pendingActions.has(questionId)) return false;
      setPendingActions((prev) => new Set(prev).add(questionId));
      setError(null);
      transitionLocalQuestionStatus(questionId, originalStatus, targetStatus);

      try {
        const response = await adminApi.questions.updateStatus(token, questionId, targetStatus, {
          expectedStatus: originalStatus,
          rejectionReason: reason,
          ...(note ? { rejectionNote: note } : {}),
        });

        if (response.success) {
          clearStatusWriteIssue(questionId);
          showSuccess('Question rejected');
          // P1-8: Refresh to sync with server state
          await fetchQuestions();
          return true;
        }

        const subOps = readQuestionPatchSubOps(response.error.details?.subOps);
        const statusPrefixCommitted = hasCommittedStatusSubOperation(subOps);
        const conflict = readQuestionStatusConflict(response.error);
        if (statusPrefixCommitted) {
          setRejectTarget((previous) =>
            previous?.questionId === questionId ? { ...previous, status: targetStatus } : previous
          );
          publishStatusWriteIssue({
            kind: 'partial',
            questionId,
            action: 'reject',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
            subOps,
          });
        } else if (conflict) {
          transitionLocalQuestionStatus(questionId, targetStatus, conflict.observed.status);
          setRejectTarget((previous) =>
            previous?.questionId === questionId
              ? { ...previous, status: conflict.observed.status }
              : previous
          );
          publishStatusWriteIssue({
            kind: 'conflict',
            questionId,
            action: 'reject',
            origin,
            expectedStatus: conflict.expected.status,
            observedStatus: conflict.observed.status,
            targetStatus,
            subOps: conflict.subOps,
          });
        } else if (response.error.code === 'STALE_WRITE') {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          publishStatusWriteIssue({
            kind: 'refresh_required',
            questionId,
            action: 'reject',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
            subOps,
          });
        } else if (isQuestionExpectedStateStaleTabError(response.error)) {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          publishStatusWriteIssue({
            kind: 'stale_tab',
            questionId,
            action: 'reject',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
          });
        } else if (isAmbiguousQuestionStatusFailure(response.error)) {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          publishStatusWriteIssue({
            kind: 'ambiguous',
            questionId,
            action: 'reject',
            origin,
            expectedStatus: originalStatus,
            targetStatus,
            subOps,
          });
        } else {
          transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
          clearStatusWriteIssue(questionId);
          showError(response.error.message);
          setError(response.error.message);
        }
        return false;
      } catch (err) {
        transitionLocalQuestionStatus(questionId, targetStatus, originalStatus);
        logger.error('REJECT_FAILURE', err instanceof Error ? err : null, { questionId });
        publishStatusWriteIssue({
          kind: 'ambiguous',
          questionId,
          action: 'reject',
          origin,
          expectedStatus: originalStatus,
          targetStatus,
          subOps: [],
        });
        return false;
      } finally {
        // P1-8: Clear pending state to re-enable actions
        setPendingActions((prev) => {
          const next = new Set(prev);
          next.delete(questionId);
          return next;
        });
      }
    },
    [
      token,
      pendingActions,
      fetchQuestions,
      showSuccess,
      showError,
      resolveQuestionForAction,
      actionOriginFor,
      clearStatusWriteIssue,
      publishStatusWriteIssue,
      transitionLocalQuestionStatus,
    ]
  );

  /**
   * ADR-025 A-Q1: intercept Reject — open the required-reason dialog instead
   * of mutating. Serves BOTH entry points (row actions + detail modal), which
   * share this callback. Signature stays `(id) => Promise<void>` for the
   * existing prop contracts.
   */
  const requestReject = useCallback(
    async (questionId: string, originOverride?: QuestionStatusWriteOrigin): Promise<void> => {
      const question = resolveQuestionForAction(questionId);
      if (!question || !isKnownQuestionStatus(question.status) || pendingActions.has(questionId)) {
        return;
      }
      clearStatusWriteIssue(questionId);
      setRejectOrigin(originOverride ?? actionOriginFor(questionId));
      setRejectTarget(question);
    },
    [actionOriginFor, clearStatusWriteIssue, pendingActions, resolveQuestionForAction]
  );

  const closeSuccessfulModerationContext = useCallback(
    (questionId: string): void => {
      if (workspaceEnabled) {
        if (questionId === selectedQuestionId) onQuestionSelectionChange?.();
        return;
      }
      if (selectedQuestion?.questionId === questionId) setIsModalOpen(false);
    },
    [onQuestionSelectionChange, selectedQuestion, selectedQuestionId, workspaceEnabled]
  );

  /**
   * Confirm from the dialog: run the guarded mutation, then close the dialog —
   * and close the DETAIL modal only on CONFIRMED SUCCESS (peer-review F2:
   * Cancel/failure must return the moderator to their exact context; the
   * detail modal no longer closes itself when reject is requested).
   */
  const handleRejectConfirm = useCallback(
    async (reason: QuestionRejectionReason, note?: string): Promise<void> => {
      if (!rejectTarget) return;
      const target = rejectTarget;
      if (!isKnownQuestionStatus(target.status)) return;
      setIsRejectSubmitting(true);
      try {
        const succeeded = await performReject(
          target.questionId,
          reason,
          note,
          target.status,
          rejectOrigin
        );
        if (succeeded) {
          closeSuccessfulModerationContext(target.questionId);
          setRejectTarget(null);
        }
      } finally {
        setIsRejectSubmitting(false);
      }
    },
    [rejectTarget, rejectOrigin, performReject, closeSuccessfulModerationContext]
  );

  const handleRejectCancel = useCallback((): void => {
    if (isRejectSubmitting) return;
    if (rejectTarget) clearStatusWriteIssue(rejectTarget.questionId);
    setRejectTarget(null);
  }, [clearStatusWriteIssue, isRejectSubmitting, rejectTarget]);

  const handleApproveStatusReapply = useCallback(
    async (issue: QuestionStatusWriteIssue): Promise<void> => {
      if (
        issue.action !== 'approve' ||
        issue.kind !== 'conflict' ||
        !canReapplyQuestionStatusIssue(issue)
      ) {
        return;
      }
      const succeeded = await handleApprove(issue.questionId, issue.observedStatus, issue.origin);
      if (succeeded && issue.origin !== 'queue') {
        closeSuccessfulModerationContext(issue.questionId);
      }
    },
    [closeSuccessfulModerationContext, handleApprove]
  );

  const handleStatusWriteDismiss = useCallback(
    (issue: QuestionStatusWriteIssue): void => {
      clearStatusWriteIssue(issue.questionId);
      if (issue.action !== 'reject') return;

      const committedTargetStillCurrent =
        hasCommittedQuestionStatusPrefix(issue) &&
        (issue.kind !== 'partial' ||
          issue.observedStatus === undefined ||
          issue.observedStatus === issue.targetStatus);
      if (isConvergedQuestionStatusIssue(issue) || committedTargetStillCurrent) {
        setRejectTarget(null);
        closeSuccessfulModerationContext(issue.questionId);
      } else if (!canReapplyQuestionStatusIssue(issue)) {
        // The operator explicitly accepted a current state from which rejection
        // is no longer legal. Close only the preserved draft dialog; the detail
        // or workspace context remains available for a different action.
        setRejectTarget(null);
      }
    },
    [clearStatusWriteIssue, closeSuccessfulModerationContext]
  );

  const handleStatusWriteRefresh = useCallback(
    async (issue: QuestionStatusWriteIssue): Promise<void> => {
      if (issue.kind === 'stale_tab') {
        window.location.reload();
        return;
      }
      if (
        (issue.kind !== 'ambiguous' &&
          issue.kind !== 'refresh_required' &&
          issue.kind !== 'partial') ||
        !token ||
        pendingActions.has(issue.questionId)
      ) {
        return;
      }

      setPendingActions((previous) => new Set(previous).add(issue.questionId));
      try {
        const response = await adminApi.questions.getById(token, issue.questionId);
        const observedValue = response.success ? response.data.question.status : undefined;
        if (
          !response.success ||
          typeof observedValue !== 'string' ||
          !isKnownQuestionStatus(observedValue)
        ) {
          showError('Could not refresh the question status. Try again.');
          return;
        }

        const observedStatus = observedValue;
        const localQuestion = resolveQuestionForAction(issue.questionId);
        transitionLocalQuestionStatus(
          issue.questionId,
          localQuestion && isKnownQuestionStatus(localQuestion.status)
            ? localQuestion.status
            : issue.expectedStatus,
          observedStatus
        );
        setRejectTarget((previous) =>
          previous?.questionId === issue.questionId
            ? { ...previous, status: observedStatus }
            : previous
        );
        if (observedStatus === issue.targetStatus) {
          clearStatusWriteIssue(issue.questionId);
          showSuccess('Question status confirmed.');
          if (issue.action === 'reject') setRejectTarget(null);
          if (issue.origin !== 'queue' || issue.action === 'reject') {
            closeSuccessfulModerationContext(issue.questionId);
          }
        } else {
          if (issue.kind === 'partial') {
            publishStatusWriteIssue({ ...issue, observedStatus });
          } else {
            // The GET resolved ambiguity without discarding the original
            // intent. Keep the same event/announcement identity and expose a
            // legal direct reapply only when the transition graph permits it.
            publishStatusWriteIssue({
              ...issue,
              kind: 'conflict',
              observedStatus,
              subOps: issue.subOps,
            });
          }
          showSuccess('Question status refreshed. Review and try again when ready.');
        }
      } catch (err) {
        logger.error('QUESTION_STATUS_REFRESH_FAILURE', err instanceof Error ? err : null, {
          questionId: issue.questionId,
        });
        showError('Could not refresh the question status. Try again.');
      } finally {
        setPendingActions((previous) => {
          const next = new Set(previous);
          next.delete(issue.questionId);
          return next;
        });
      }
    },
    [
      clearStatusWriteIssue,
      closeSuccessfulModerationContext,
      pendingActions,
      publishStatusWriteIssue,
      resolveQuestionForAction,
      showError,
      showSuccess,
      token,
      transitionLocalQuestionStatus,
    ]
  );

  /**
   * Handle refresh button click.
   */
  const handleRefresh = useCallback((): void => {
    if (!queueInteractionLocked) fetchQuestions();
  }, [fetchQuestions, queueInteractionLocked]);

  // Row-level moderation of the SELECTED question is another context-changing
  // entry point. Route it through the same draft guard as workspace controls;
  // other rows remain independent and must not be blocked by this workspace.
  const requestQueueModeration = useCallback(
    (questionId: string, proceed: () => void): void => {
      const workspace = workspaceRef.current;
      if (workspaceEnabled && questionId === selectedQuestionId && workspace) {
        workspace.requestTransition(proceed);
        return;
      }
      proceed();
    },
    [selectedQuestionId, workspaceEnabled]
  );

  const handleQueueApprove = useCallback(
    (questionId: string): Promise<void> => {
      requestQueueModeration(questionId, () => {
        void handleApprove(questionId, undefined, 'queue');
      });
      return Promise.resolve();
    },
    [handleApprove, requestQueueModeration]
  );

  const handleQueueReject = useCallback(
    (questionId: string): Promise<void> => {
      requestQueueModeration(questionId, () => {
        void requestReject(questionId, 'queue');
      });
      return Promise.resolve();
    },
    [requestQueueModeration, requestReject]
  );

  const handleWorkspaceApprove = useCallback(
    (questionId: string): Promise<boolean> => handleApprove(questionId, undefined, 'workspace'),
    [handleApprove]
  );

  const handleWorkspaceReject = useCallback(
    (questionId: string): Promise<void> => requestReject(questionId, 'workspace'),
    [requestReject]
  );

  const handleDetailApprove = useCallback(
    (questionId: string): Promise<boolean> => handleApprove(questionId, undefined, 'detail'),
    [handleApprove]
  );

  const handleDetailReject = useCallback(
    (questionId: string): Promise<void> => requestReject(questionId, 'detail'),
    [requestReject]
  );

  /**
   * Handle view question details.
   */
  const handleViewQuestion = useCallback(
    (question: AdminQuestionListItem): void => {
      if (queueInteractionLocked) return;
      if (workspaceEnabled) {
        if (isNarrowWorkspace && !workspaceQuestionId) {
          narrowQueueViewport.current = { left: window.scrollX, top: window.scrollY };
        }
        onQuestionSelectionChange?.(question.questionId);
        return;
      }
      setSelectedQuestion(question);
      setIsModalOpen(true);
    },
    [
      isNarrowWorkspace,
      onQuestionSelectionChange,
      queueInteractionLocked,
      workspaceEnabled,
      workspaceQuestionId,
    ]
  );

  /**
   * Handle close modal. selectedQuestion is deliberately KEPT: nulling it
   * unmounts QuestionDetailModal in the same commit, which would skip A-0's
   * open→closed focus restore (ext-R1 M11 retrofit). The media hook inside is
   * `enabled`-gated on isOpen, so a closed-but-mounted modal does no work.
   */
  const handleCloseModal = useCallback((): void => {
    if (workspaceEnabled) {
      onQuestionSelectionChange?.();
      return;
    }
    setIsModalOpen(false);
  }, [workspaceEnabled, onQuestionSelectionChange]);

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <Panel data-testid={testId}>
        <Box padding="medium">
          <Text data-testid={testId ? `${testId}-not-authenticated` : undefined}>
            Please log in through BigCommerce to view questions.
          </Text>
        </Box>
      </Panel>
    );
  }

  /**
   * Cell renderers, keyed by QUEUE_COLUMN_SPEC hash (design-v3 §H). The
   * `Record<QueueColumnHash, …>` type makes adding a spec entry a compile
   * error until its renderer exists. Bodies are the pre-B-6 inline column
   * renders, verbatim — only their packaging moved.
   */
  const columnRenderers: Record<
    QueueColumnHash,
    (item: AdminQuestionListItem) => React.ReactElement
  > = {
    title: ({ title, description, questionId, replyCount }) => (
      <div style={pendingActions.has(questionId) ? { opacity: 0.5 } : undefined}>
        <Text bold>
          {pendingActions.has(questionId) ? '...' : ''}
          {title}
        </Text>
        {description && (
          <Text color="secondary">{truncateText(description, MAX_DESCRIPTION_LENGTH)}</Text>
        )}
        {replyCount === 0 && <Text>Unanswered</Text>}
      </div>
    ),
    poster: ({ posterName }) => <Text>{posterName}</Text>,
    date: ({ createdAt }) => <Text title={formatDate(createdAt)}>{formatDate(createdAt)}</Text>,
    replies: ({ replyCount, pendingReplyCount }) => (
      <Flex flexDirection="column" flexGap="2px">
        <Text>{replyCount}</Text>
        {pendingReplyCount > 0 && (
          <Text bold data-testid={testId ? `${testId}-pending-replies-badge` : undefined}>
            {pendingReplyCount} pending review
          </Text>
        )}
      </Flex>
    ),
    images: ({ images, pendingImageCount }) => (
      <Flex flexDirection="column" flexGap="2px">
        <Text>{images?.length ?? 0}</Text>
        {pendingImageCount > 0 && (
          <Text bold data-testid={testId ? `${testId}-pending-images-badge` : undefined}>
            {pendingImageCount} pending review
          </Text>
        )}
      </Flex>
    ),
    quality: ({ qualityScore }) => <QualityBadge qualityScore={qualityScore} />,
    status: ({ status }) => <StatusBadge status={status} />,
    actions: (question) => (
      <QuestionActions
        question={question}
        onApprove={handleQueueApprove}
        onReject={handleQueueReject}
        onView={handleViewQuestion}
        pendingActions={pendingActions}
        navigationDisabled={queueInteractionLocked}
        testId={testId}
      />
    ),
  };

  const workspaceStatusWriteIssue = workspaceQuestion
    ? statusWriteIssues[workspaceQuestion.questionId]
    : undefined;
  const visibleWorkspaceStatusWriteIssue =
    workspaceStatusWriteIssue?.action === 'approve' &&
    workspaceStatusWriteIssue.origin === 'workspace'
      ? workspaceStatusWriteIssue
      : null;
  const detailStatusWriteIssue = selectedQuestion
    ? statusWriteIssues[selectedQuestion.questionId]
    : undefined;
  const visibleDetailStatusWriteIssue =
    detailStatusWriteIssue?.action === 'approve' && detailStatusWriteIssue.origin === 'detail'
      ? detailStatusWriteIssue
      : null;
  const rejectStatusWriteIssue = rejectTarget
    ? statusWriteIssues[rejectTarget.questionId]
    : undefined;
  const visibleRejectStatusWriteIssue =
    rejectStatusWriteIssue?.action === 'reject' ? rejectStatusWriteIssue : null;

  const queuePanel = (
    <Panel data-testid={testId}>
      {/* Header */}
      <PageHeader
        heading={<H2 marginBottom="none">Question Queue</H2>}
        action={
          <Button
            ref={refreshBtnRef}
            variant="subtle"
            onClick={handleRefresh}
            disabled={isLoading || queueInteractionLocked}
            iconLeft={<RefreshIcon />}
            data-testid={testId ? `${testId}-refresh-btn` : undefined}
          >
            Refresh
          </Button>
        }
      />

      {/* One persistent, initially-empty alert per loaded row. Only queue-
          originated approvals populate it; reject outcomes stay with the
          preserved rejection draft inside its dialog. */}
      <Box paddingHorizontal="medium">
        {questions.map((question) => {
          const issue = statusWriteIssues[question.questionId];
          const visibleIssue =
            issue?.action === 'approve' && issue.origin === 'queue' ? issue : null;
          return (
            <QuestionStatusWriteNotice
              key={question.questionId}
              issue={visibleIssue}
              busy={pendingActions.has(question.questionId)}
              onReapply={
                visibleIssue?.kind === 'conflict'
                  ? () => void handleApproveStatusReapply(visibleIssue)
                  : undefined
              }
              onRefresh={
                visibleIssue?.kind === 'refresh_required' ||
                visibleIssue?.kind === 'stale_tab' ||
                visibleIssue?.kind === 'ambiguous' ||
                visibleIssue?.kind === 'partial'
                  ? () => void handleStatusWriteRefresh(visibleIssue)
                  : undefined
              }
              onDismiss={
                visibleIssue?.kind === 'conflict' || visibleIssue?.kind === 'partial'
                  ? () => handleStatusWriteDismiss(visibleIssue)
                  : undefined
              }
              restoreFocusTo={() => restoreQueueStatusFocus(question.questionId)}
              testId={`${testId ?? 'question-queue'}-row-${question.questionId}-status-write`}
            />
          );
        })}
      </Box>

      {/* Stats */}
      {stats && (
        <Box padding="medium" backgroundColor="secondary10">
          <Flex flexGap="2rem">
            <FlexItem>
              <Text bold data-testid={testId ? `${testId}-pending-count` : undefined}>
                {stats.pendingCount} Pending
              </Text>
            </FlexItem>
            <FlexItem>
              <Text bold data-testid={testId ? `${testId}-open-count` : undefined}>
                {stats.openCount} Open
              </Text>
            </FlexItem>
            <FlexItem>
              <Text bold data-testid={testId ? `${testId}-resolved-count` : undefined}>
                {stats.resolvedCount} Resolved
              </Text>
            </FlexItem>
            <FlexItem>
              <Text bold data-testid={testId ? `${testId}-pending-replies-count` : undefined}>
                {stats.pendingRepliesCount} Replies to review
              </Text>
            </FlexItem>
          </Flex>
        </Box>
      )}

      {/* Filters (P1-17: Extracted component) */}
      <QuestionFilters
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        unansweredOnly={unansweredOnly}
        onUnansweredChange={handleUnansweredChange}
        hasImagesOnly={hasImagesOnly}
        onHasImagesChange={handleHasImagesChange}
        needsReviewOnly={needsReviewOnly}
        onNeedsReviewChange={handleNeedsReviewChange}
        pendingImagesOnly={pendingImagesOnly}
        onPendingImagesChange={handlePendingImagesChange}
        statusFilter={statusFilter}
        onStatusChange={handleStatusChange}
        sortValue={sortValue}
        onSortChange={handleSortChange}
        disabled={queueInteractionLocked}
        testId={testId}
      />

      {/* Error */}
      {error && (
        <Box padding="medium">
          <Text color="danger" data-testid={testId ? `${testId}-error` : undefined}>
            Error: {error}
          </Text>
        </Box>
      )}

      {/* Loading Skeleton (P1-17: Extracted component; B-6 §H: cells follow
          QUEUE_COLUMN_SPEC so the skeleton mirrors the real table's shape) */}
      {isLoading && <TableSkeleton testId={testId} columns={QUEUE_SKELETON_COLUMNS} />}

      {/* Empty State */}
      {!isLoading && !error && questions.length === 0 && (
        <EmptyState
          variant="plain"
          testId={testId ? `${testId}-empty` : undefined}
          body="No questions found."
        />
      )}

      {/* Questions Table */}
      {!isLoading && !error && questions.length > 0 && (
        <Box>
          {/* ADR-025 A-H (M13): the table scrolls INSIDE this container — the
              page body must never scroll sideways at supported widths. */}
          <Box data-testid="queue-table-scroll" style={{ overflowX: 'auto' }}>
            <Table
              /* design-v3 §H: identity + shape from QUEUE_COLUMN_SPEC, cell
                 content from columnRenderers — one source shared with the
                 skeleton. No queue column declares a width today, so the
                 spread adds nothing yet; a future fixed-width entry flows to
                 Table and skeleton in the same edit. */
              columns={QUEUE_COLUMN_SPEC.map(({ hash, header, shape }) => ({
                hash,
                header,
                render: columnRenderers[hash],
                ...(shape !== 'flex' ? { width: shape.width } : {}),
              }))}
              items={questions}
              itemName="questions"
              keyField="questionId"
              data-testid={testId ? `${testId}-table` : undefined}
            />
          </Box>
        </Box>
      )}

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <Box padding="medium">
          <Toolbar preset="pagination">
            <FlexItem>
              <Text data-testid={testId ? `${testId}-pagination-info` : undefined}>
                Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
              </Text>
            </FlexItem>
            <FlexItem>
              <Toolbar flexGap="0.5rem">
                <FlexItem>
                  <Button
                    variant="secondary"
                    onClick={handlePrevPage}
                    disabled={currentPage === 1 || isLoading || queueInteractionLocked}
                    iconLeft={<ArrowBackIcon />}
                    data-testid={testId ? `${testId}-prev-btn` : undefined}
                  >
                    Previous
                  </Button>
                </FlexItem>
                <FlexItem>
                  <Button
                    variant="secondary"
                    onClick={handleNextPage}
                    disabled={
                      currentPage >= pagination.totalPages || isLoading || queueInteractionLocked
                    }
                    iconLeft={<ArrowForwardIcon />}
                    data-testid={testId ? `${testId}-next-btn` : undefined}
                  >
                    Next
                  </Button>
                </FlexItem>
              </Toolbar>
            </FlexItem>
          </Toolbar>
        </Box>
      )}
    </Panel>
  );

  const workspace = workspaceQuestion ? (
    <QuestionWorkspace
      key={workspaceQuestion.questionId}
      ref={workspaceRef}
      question={workspaceQuestion}
      token={token ?? ''}
      onClose={handleCloseModal}
      onApprove={handleWorkspaceApprove}
      onReject={handleWorkspaceReject}
      pendingActions={pendingActions}
      statusWriteIssue={visibleWorkspaceStatusWriteIssue}
      onStatusWriteReapply={
        visibleWorkspaceStatusWriteIssue?.kind === 'conflict'
          ? () => void handleApproveStatusReapply(visibleWorkspaceStatusWriteIssue)
          : undefined
      }
      onStatusWriteRefresh={
        visibleWorkspaceStatusWriteIssue?.kind === 'refresh_required' ||
        visibleWorkspaceStatusWriteIssue?.kind === 'stale_tab' ||
        visibleWorkspaceStatusWriteIssue?.kind === 'ambiguous' ||
        visibleWorkspaceStatusWriteIssue?.kind === 'partial'
          ? () => void handleStatusWriteRefresh(visibleWorkspaceStatusWriteIssue)
          : undefined
      }
      onStatusWriteDismiss={
        visibleWorkspaceStatusWriteIssue?.kind === 'conflict' ||
        visibleWorkspaceStatusWriteIssue?.kind === 'partial'
          ? () => handleStatusWriteDismiss(visibleWorkspaceStatusWriteIssue)
          : undefined
      }
      externalNavigationGuard={Boolean(registerNavigationGuard)}
      isActive={isActive}
      testId={testId ? `${testId}-workspace` : undefined}
    />
  ) : null;

  return (
    <>
      {workspace ? (
        isNarrowWorkspace ? (
          <div
            data-testid={testId ? `${testId}-workspace-layout` : undefined}
            data-layout="full-page"
            style={{ minWidth: 0, maxWidth: '100%' }}
          >
            {workspace}
          </div>
        ) : (
          <div
            data-testid={testId ? `${testId}-workspace-layout` : undefined}
            data-layout="split"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(360px, 42%) minmax(0, 1fr)',
              gap: tokens.dimension.space.lg,
              alignItems: 'start',
              minWidth: 0,
              maxWidth: '100%',
            }}
          >
            <div style={{ minWidth: 0 }}>{queuePanel}</div>
            <div style={{ minWidth: 0 }}>{workspace}</div>
          </div>
        )
      ) : (
        queuePanel
      )}

      <QuestionRejectModal
        isOpen={rejectTarget !== null}
        questionTitle={rejectTarget?.title}
        isSubmitting={
          isRejectSubmitting || (rejectTarget ? pendingActions.has(rejectTarget.questionId) : false)
        }
        onConfirm={handleRejectConfirm}
        onCancel={handleRejectCancel}
        statusWriteIssue={visibleRejectStatusWriteIssue}
        onStatusWriteReapply={handleRejectConfirm}
        onStatusWriteRefresh={
          visibleRejectStatusWriteIssue?.kind === 'refresh_required' ||
          visibleRejectStatusWriteIssue?.kind === 'stale_tab' ||
          visibleRejectStatusWriteIssue?.kind === 'ambiguous' ||
          visibleRejectStatusWriteIssue?.kind === 'partial'
            ? () => void handleStatusWriteRefresh(visibleRejectStatusWriteIssue)
            : undefined
        }
        onStatusWriteDismiss={
          visibleRejectStatusWriteIssue?.kind === 'conflict' ||
          visibleRejectStatusWriteIssue?.kind === 'partial'
            ? () => handleStatusWriteDismiss(visibleRejectStatusWriteIssue)
            : undefined
        }
        fallbackFocusTo={refreshBtnRef}
        testId={testId}
      />

      {!workspaceEnabled && (
        <QuestionDetailModal
          question={selectedQuestion}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onApprove={handleDetailApprove}
          onReject={handleDetailReject}
          token={token ?? ''}
          pendingActions={pendingActions}
          statusWriteIssue={visibleDetailStatusWriteIssue}
          onStatusWriteReapply={
            visibleDetailStatusWriteIssue?.kind === 'conflict'
              ? () => void handleApproveStatusReapply(visibleDetailStatusWriteIssue)
              : undefined
          }
          onStatusWriteRefresh={
            visibleDetailStatusWriteIssue?.kind === 'refresh_required' ||
            visibleDetailStatusWriteIssue?.kind === 'stale_tab' ||
            visibleDetailStatusWriteIssue?.kind === 'ambiguous' ||
            visibleDetailStatusWriteIssue?.kind === 'partial'
              ? () => void handleStatusWriteRefresh(visibleDetailStatusWriteIssue)
              : undefined
          }
          onStatusWriteDismiss={
            visibleDetailStatusWriteIssue?.kind === 'conflict' ||
            visibleDetailStatusWriteIssue?.kind === 'partial'
              ? () => handleStatusWriteDismiss(visibleDetailStatusWriteIssue)
              : undefined
          }
          fallbackFocusTo={refreshBtnRef}
          testId={testId}
        />
      )}
    </>
  );
}
